import {useQuery} from "@tanstack/react-query";
import {useMemo} from "react";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {
    OrganizationalUnitStatus,
    type OrganizationalUnit,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnit";
import {EMOTORAD__OU_DEALERSHIP_LAT__COLUMN_ID, EMOTORAD__OU_DEALERSHIP_LONG__COLUMN_ID, EmotoradOus} from "~/domainModels/workspaceSpecific/emotorad";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {userHasAccessToOrganisationalUnit} from "~/utilities/accessControl/userOuAccess";
import {getGeographyDetailsByPincode} from "~/utilities/apis/geography";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";

function haversineDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toCoordinate(raw: unknown): number | null {
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return raw;
    }
    if (typeof raw === "string") {
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

function getOrganizationalUnitDisplayName(ou: OrganizationalUnit, organizationalUnitTemplates: ReturnType<typeof useOrganizationalUnitTemplates>): string {
    const template = organizationalUnitTemplates.find((item) => item.id === ou.templateId);
    if (template === undefined) {
        return "";
    }
    return processOttopilotStringTemplate(template.instanceDisplayNameTemplate, ou.additionalColumnValues).trim();
}

export function EmotoradAssignedDealerSelect({
    customerPincode,
    value,
    onChange,
    isDisabled = false,
}: {
    customerPincode: string | null;
    value: Uuid | null;
    onChange: (dealerId: Uuid | null) => void;
    isDisabled?: boolean;
}) {
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspaceUser, heimdallUser} = useWorkspaceUser();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();

    const accessibleDealershipOus = useMemo(() => {
        return organizationalUnits.filter(
            (ou) =>
                ou.templateId === (EmotoradOus.Dealership as Uuid) &&
                ou.status === OrganizationalUnitStatus.active &&
                userHasAccessToOrganisationalUnit({
                    targetUser: workspaceUser,
                    users: users,
                    userTemplates: userTemplates,
                    organizationalUnits: organizationalUnits,
                    organizationalUnitTemplates: organizationalUnitTemplates,
                    targetOrganisationalUnitId: ou.id,
                }) === true,
        );
    }, [organizationalUnits, organizationalUnitTemplates, workspaceUser, users, userTemplates]);

    const normalizedPincode = customerPincode !== null && customerPincode.trim() !== "" ? customerPincode.trim() : null;

    const geoQuery = useQuery({
        queryKey: ["emotorad", "assignedDealerSelect", "geoDetails", normalizedPincode],
        queryFn: async () => {
            return getGeographyDetailsByPincode({
                pincode: normalizedPincode ?? "",
                dialCode: "+91",
                intellsysBaseUrl: intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
            });
        },
        enabled: normalizedPincode !== null,
        retry: false,
    });

    const dealershipOptions = useMemo(() => {
        const custLat = geoQuery.data?.latitude ?? null;
        const custLng = geoQuery.data?.longitude ?? null;

        if (custLat === null || custLng === null) {
            return [];
        }

        return accessibleDealershipOus
            .map((ou) => {
                const ouLat = toCoordinate(ou.additionalColumnValues[EMOTORAD__OU_DEALERSHIP_LAT__COLUMN_ID]);
                const ouLng = toCoordinate(ou.additionalColumnValues[EMOTORAD__OU_DEALERSHIP_LONG__COLUMN_ID]);
                if (ouLat === null || ouLng === null) {
                    return null;
                }
                const distance = haversineDistanceKm(custLat, custLng, ouLat, ouLng);
                return {ou: ou, distance: distance};
            })
            .filter((item): item is {ou: OrganizationalUnit; distance: number} => item !== null)
            .filter((item) => item.distance <= 50)
            .sort((left, right) => left.distance - right.distance)
            .map(({ou}) => {
                const label = getOrganizationalUnitDisplayName(ou, organizationalUnitTemplates);
                if (label === "") {
                    return null;
                }
                return {label: label, value: ou.id};
            })
            .filter((item): item is {label: string; value: Uuid} => item !== null);
    }, [accessibleDealershipOus, geoQuery.data, organizationalUnitTemplates]);

    const selectedOption = useMemo(() => {
        if (value === null) {
            return null;
        }
        const fromOptions = dealershipOptions.find((option) => option.value === value);
        if (fromOptions !== undefined) {
            return fromOptions;
        }
        const ou = organizationalUnits.find((unit) => unit.id === value);
        if (ou === undefined) {
            return {label: value, value: value};
        }
        const label = getOrganizationalUnitDisplayName(ou, organizationalUnitTemplates);
        return {label: label !== "" ? label : value, value: value};
    }, [value, dealershipOptions, organizationalUnits, organizationalUnitTemplates]);

    const placeholder =
        normalizedPincode === null
            ? "Customer pincode required"
            : geoQuery.isLoading === true
              ? "Loading dealerships..."
              : dealershipOptions.length === 0
                ? "No dealerships within 50 km"
                : "Select dealership";

    return (
        <AnankeSelect
            isDisabled={isDisabled === true || normalizedPincode === null || geoQuery.isLoading === true}
            placeholder={placeholder}
            options={dealershipOptions}
            value={selectedOption}
            onChange={(selected: unknown) => {
                if (selected === null || selected === undefined) {
                    onChange(null);
                    return;
                }
                onChange((selected as {value: Uuid}).value);
            }}
        />
    );
}
